import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { getAllRoutes } from './Login/login.async'
import * as CryptoJS from 'crypto-js'

// Define your encryption key
let ENCRYPTION_KEY = import.meta.env.ENCRYPTION_KEY || 'ad00f82eed9f2e01e7a1e4e44ab71579'

// Function to encrypt data
const encryptData = (data) => {
  const dataToEncrypt = typeof data === 'string' ? data : JSON.stringify(data)

  try {
    const encryptedData = CryptoJS.AES.encrypt(dataToEncrypt, ENCRYPTION_KEY).toString()
    // console.log('Encrypted Data:', encryptedData);  // Log the encrypted data
    return encryptedData
  } catch (err) {
    console.error('Error during encryption:', err)
    return '' // Return empty string on error
  }
}

// Function to decrypt data
const decryptData = (encryptedData) => {
  if (!encryptedData) {
    console.error('No encrypted data provided for decryption')
    return []
  }

  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY)
    const decrypted = bytes.toString(CryptoJS.enc.Utf8)

    if (!decrypted) {
      console.error('Decryption failed, no valid output')
      return [] // Return empty array if decryption fails
    }

    try {
      const parsedData = JSON.parse(decrypted)
      // console.log('Decrypted Data:', parsedData); // Log the decrypted data
      return parsedData
    } catch (err) {
      console.error('Failed to parse decrypted data:', err)
      return [] // Return empty array if parsing fails
    }
  } catch (err) {
    console.error('Error during decryption:', err)
    return [] // Return empty array if decryption fails
  }
}

// Helper function to get stored config IDs from localStorage
const getStoredConfigID = () => {
  const storedToken = localStorage.getItem('configIDs')
  if (storedToken) {
    // console.log('Stored configIDs:', storedToken) // Log for debugging
  } else {
    // console.log('No configIDs in localStorage')
  }
  return storedToken ? decryptData(storedToken) : []
}

const initialState = {
  allRouteList: [],
  routeListData: [],
  isAccessDenied: false,
  allRouteListLoader: false,
  configID: getStoredConfigID(), // Storing decrypted config IDs
  permissions: [],
}

export const routeSlice = createSlice({
  name: 'reduxSlice',
  initialState,
  extraReducers: (builder) => {
    builder.addMatcher(isAnyOf(getAllRoutes.pending), (state) => {
      state.allRouteListLoader = true
    })

    builder.addMatcher(isAnyOf(getAllRoutes.fulfilled), (state, action) => {
      // API may return { menu_items: [...], permissions: [...] } or a bare array.
      const menuItems = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.menu_items ?? null
      const rawPermissions = Array.isArray(action.payload) ? [] : action.payload?.permissions || []
      const {
        isGenAI,
        isWhatIf,
        isBottomUpModelGeneration,
        isExtrapolation,
        isShortlistForIFA,
        isDataEnrichment,
      } = rawPermissions.reduce(
        (acc, item) => {
          if (item.permission_name === 'GenAI Should Cost Model creation') {
            acc.isGenAI = true
          }
          if (item.permission_name === 'What-if Simulation') {
            acc.isWhatIf = true
          }
          if (item.permission_name === 'Bottom-up Model Generation') {
            acc.isBottomUpModelGeneration = true
          }
          if (item.permission_name === 'Extrapolation') {
            acc.isExtrapolation = true
          }
          if (item.permission_name === 'Shortlist for IFA') {
            acc.isShortlistForIFA = true
          }
          if (item.permission_name === 'AI Data Enrichment') {
            acc.isDataEnrichment = true
          }
          return acc
        },
        {
          isGenAI: false,
          isWhatIf: false,
          isBottomUpModelGeneration: false,
          isExtrapolation: false,
          isShortlistForIFA: false,
          isDataEnrichment: false,
        },
      )
      localStorage.setItem('top_down_creation_preference', isGenAI)
      localStorage.setItem('is_show_top_down_creation_preference', isGenAI)
      localStorage.setItem('what_if_preference', isWhatIf)
      localStorage.setItem('bottom_up_model_generation_preference', isBottomUpModelGeneration)
      localStorage.setItem('extrapolation_preference', isExtrapolation)
      localStorage.setItem('shortlist_for_ifa_preference', isShortlistForIFA)
      localStorage.setItem('data_enrichment_preference', isDataEnrichment)

      state.permissions = rawPermissions.map((permission) => permission.permission_name)
      /**
       * Walks through children in their original API order.
       * When a `group_name` group starts, inserts a divider + section header.
       * When a `group_name` group ends, inserts a trailing divider.
       * No reordering — the backend controls the sequence via `order_no`.
       */
      const injectGroupSections = (menuItems) => {
        return menuItems.map((item) => {
          if (!item?.children?.length) return item

          const newChildren = []
          let currentGroup = null

          const createDivider = (type, key) => ({
            nav_type: 'divider',
            nav_id: `${type}-${key}`,
          })

          const createSectionHeader = (key, label) => ({
            nav_type: 'section_header',
            nav_id: `section-${key}`,
            config_label: label,
          })

          for (const child of item.children) {
            const groupName = child.group_name || null

            if (groupName !== currentGroup) {
              // Close previous group if it exists
              if (currentGroup) {
                const prevKey = currentGroup.toLowerCase().replace(/\s+/g, '-')
                newChildren.push(createDivider('divider-after', prevKey))
              }

              // Open new group if it exists
              if (groupName) {
                const sectionKey = groupName.toLowerCase().replace(/\s+/g, '-')
                newChildren.push(createDivider('divider-before', sectionKey))
                newChildren.push(createSectionHeader(sectionKey, groupName))
              }

              currentGroup = groupName
            }

            newChildren.push(child)
          }

          // Close last group if needed
          if (currentGroup) {
            const prevKey = currentGroup.toLowerCase().replace(/\s+/g, '-')
            newChildren.push(createDivider('divider-after', prevKey))
          }

          return { ...item, children: newChildren }
        })
      }

      const flattenMenu = (items = []) => {
        return items.reduce((acc, item) => {
          acc.push(item)
          if (item.children?.length) {
            acc.push(...flattenMenu(item.children))
          }
          return acc
        }, [])
      }

      // Only overwrite route state when the payload actually contains route data.
      // If absent (partial/error response), preserve whatever was already loaded
      // so the sidebar does not go blank.
      if (menuItems != null) {
        const processedPayload = injectGroupSections(menuItems)
        state.routeListData = processedPayload

        const flat = flattenMenu(menuItems)

        state.allRouteList = flat

        const configIDs = menuItems.map((route) => ({
          route: route.id,
          access_level: route?.access_level,
        }))
        state.configID = configIDs

        const encryptedConfigIDs = encryptData(configIDs)
        if (encryptedConfigIDs) {
          localStorage.setItem('configIDs', encryptedConfigIDs)
        } else {
          console.error('Failed to encrypt configIDs')
        }
      }

      state.allRouteListLoader = false
    })

    builder.addMatcher(isAnyOf(getAllRoutes.rejected), (state) => {
      state.allRouteList = []
      state.routeListData = []
      state.configID = []
      state.allRouteListLoader = false
    })
  },

  reducers: {
    setAccessDenied: (state, action) => {
      state.isAccessDenied = action.payload
    },
  },
})

export const { setAccessDenied } = routeSlice.actions

export default routeSlice.reducer
